/**
 * useTillPricing — the storefront cart priced exactly as the POS till prices
 * it (owner ruling 2026-10-10: the till is authoritative). Thin React glue
 * around the pure `priceCart` (src/lib/till-pricing.ts): reads the cart, the
 * entered promo code, the branch's fees/taxes, the fulfilment mode and the
 * branch wall clock, and re-prices every 30s so a happy hour opens and closes
 * on its own.
 *
 * Menu items and promotions are passed in (screens already load them; calling
 * useMenu/usePromotions again would refetch).
 */
import { useEffect, useMemo, useState } from "react";

import type { Promotion } from "@/api/online-ordering-service/model/promotion";
import { OrderTypeEnum } from "@/api/online-ordering-service/model/orderTypeEnum";
import type { UiMenuItem } from "@/hooks/useMenu";
import { getBranchTime } from "@/lib/branch-time";
import {
  useDeliveryFee,
  useDeliveryTaxes,
  useDineInServiceChargePercent,
  useServiceFeeTaxes,
} from "@/hooks/useBranchInfo";
import { priceCart, type PriceCartResult } from "@/lib/till-pricing";
import type { SurchargeDefinition, TaxRef } from "@/lib/surcharges";
import { useBranch } from "@/store/branch";
import { useCart } from "@/store/cart";
import { useFulfillment } from "@/store/fulfillment";
import { useTable } from "@/store/table";

export type UseTillPricingArgs = {
  menuItems: ReadonlyArray<UiMenuItem>;
  menuTaxExempt?: boolean;
  promotions: ReadonlyArray<Promotion>;
  /** Requested loyalty redemption (money); priceCart caps it at the bill. */
  loyaltyRedemption?: number;
  surcharges?: ReadonlyArray<SurchargeDefinition> | null;
  surchargeTaxesById?: Record<string, TaxRef>;
  deliveryCoordinates?: { lat: number; lng: number } | null;
};

export type UseTillPricingResult = {
  pricing: PriceCartResult;
  /** The promotions the till applied, in application order. */
  appliedPromotions: Promotion[];
  /** The first code the guest entered, if any. */
  enteredCode: string | null;
  /** Every code the guest entered, in the order applied. */
  enteredCodes: string[];
  orderType: OrderTypeEnum;
};

/** The order type the storefront sends for the current fulfilment. */
export function storefrontOrderType(isDineIn: boolean, mode: string): OrderTypeEnum {
  if (isDineIn) return OrderTypeEnum.DineIn;
  return mode === "pickup" ? OrderTypeEnum.Pickup : OrderTypeEnum.Delivery;
}

/** The promotion list priceCart sees: the branch's active list plus the entered-code promotions. */
export function promotionsWithEntered(
  promotions: ReadonlyArray<Promotion>,
  entered: Promotion | ReadonlyArray<Promotion> | null | undefined,
): Promotion[] {
  const list: ReadonlyArray<Promotion> = !entered ? [] : Array.isArray(entered) ? entered : [entered as Promotion];
  const out = [...promotions];
  for (const e of list) {
    if (e.id && out.some((p) => p.id === e.id)) continue;
    out.push(e);
  }
  return out;
}

export function useTillPricing(args: UseTillPricingArgs): UseTillPricingResult {
  const { cart, appliedPromotions: entered } = useCart();
  const { branchId, branch } = useBranch();
  const { isDineIn } = useTable();
  const { mode } = useFulfillment();
  const deliveryFee = useDeliveryFee();
  const deliveryTaxes = useDeliveryTaxes();
  const serviceChargePercent = useDineInServiceChargePercent();
  const serviceFeeTaxes = useServiceFeeTaxes();
  const timezone = (branch as { timezone?: string | null } | null | undefined)?.timezone ?? null;

  // Re-price on the minute boundary cadence (promotion windows are minute-granular).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const orderType = storefrontOrderType(isDineIn, mode);
  const enteredCodes = useMemo(
    () => entered.map((p) => p.promoCode?.trim() ?? "").filter((c) => c),
    [entered],
  );
  const enteredCode = enteredCodes[0] ?? null;
  const {
    menuItems, menuTaxExempt, promotions, loyaltyRedemption, surcharges, surchargeTaxesById, deliveryCoordinates,
  } = args;

  const pricing = useMemo(
    () =>
      priceCart({
        cart,
        menuItems,
        menuTaxExempt,
        promotions: promotionsWithEntered(promotions, entered),
        enteredCodes,
        orderType,
        channel: "online",
        branchId: branchId ?? "",
        now: getBranchTime(timezone),
        serviceChargePercent,
        serviceFeeTaxes,
        deliveryFee,
        deliveryTaxes,
        loyaltyRedemption,
        deliveryCoordinates,
        surcharges,
        surchargeTaxesById,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cart, menuItems, menuTaxExempt, promotions, entered, enteredCodes, orderType, branchId, timezone,
      serviceChargePercent, serviceFeeTaxes, deliveryFee, deliveryTaxes, loyaltyRedemption, deliveryCoordinates,
      surcharges, surchargeTaxesById, tick,
    ],
  );

  const appliedPromotions = useMemo(() => {
    const all = promotionsWithEntered(promotions, entered);
    return pricing.appliedPromotionIds
      .map((id) => all.find((p) => p.id === id))
      .filter((p): p is Promotion => !!p);
  }, [pricing.appliedPromotionIds, promotions, entered]);

  return { pricing, appliedPromotions, enteredCode, enteredCodes, orderType };
}
